// Übersetzung der Impressums-Felder („Daten befüllen") auf die Kundenakte.
//
// Der Anreicherungs-Dienst liefert die Feldnamen der ANLAGE-Maske. Die Akte
// heißt an mehreren Stellen anders — der Firmenname steht dort in `lastname`,
// die Anschrift in `strasse`/`plz`/`ort`, und Telefon, Fax, E-Mail und Website
// sind gar keine Kontaktfelder, sondern Kommunikationswege. Ohne diese
// Übersetzung landeten die Werte im falschen Feld oder gar nicht.

import type { BefundFelder } from "../../services/firmendatenService";
import type { ContactDetailsFormValues } from "../../types/Contact";

/** Kommunikationsarten der Firma, die aus dem Impressum kommen können. */
export type AkteKommunikationsTyp = "phone" | "fax" | "email" | "homepage";

export type AkteZiel =
  | { art: "feld"; feld: keyof ContactDetailsFormValues }
  | { art: "kommunikation"; typ: AkteKommunikationsTyp };

/**
 * Jedes gelieferte Feld einzeln zugeordnet. `null` heißt: in der Akte gibt es
 * dafür keine Ablage — die Rechtsform wird aus der Auswahlliste gepflegt (der
 * Freitext „GmbH & Co. KG" passt dort nicht hinein), und „Vertreten durch" ist
 * kein Feld, sondern eine Person, die als Beziehung angelegt gehört. Beide
 * werden vom Dialog ohnehin nicht zur Übernahme angeboten (FELD_REIHENFOLGE).
 */
export const AKTE_ZIELE: Record<keyof BefundFelder, AkteZiel | null> = {
  name: { art: "feld", feld: "lastname" },
  website: { art: "kommunikation", typ: "homepage" },
  phone: { art: "kommunikation", typ: "phone" },
  fax: { art: "kommunikation", typ: "fax" },
  email: { art: "kommunikation", typ: "email" },
  handelsregisterNummer: { art: "feld", feld: "handelsregisterNummer" },
  registergericht: { art: "feld", feld: "registergericht" },
  umsatzsteuernummer: { art: "feld", feld: "umsatzsteuernummer" },
  steuernummer: { art: "feld", feld: "steuernummer" },
  street: { art: "feld", feld: "strasse" },
  postalcode: { art: "feld", feld: "plz" },
  city: { art: "feld", feld: "ort" },
  rechtsform: null,
  vertretung: null,
};

/**
 * Was in der Akte bereits steht — je Feld des Anreicherungs-Dienstes. Der Dialog
 * markiert damit die belegten Felder und wählt nur leere vor; ohne diese
 * Rückübersetzung sähe er eine gepflegte Akte als leer an und böte an, alles zu
 * überschreiben.
 */
export function vorhandeneAkteWerte(
  feldWert: (feld: keyof ContactDetailsFormValues) => string,
  kommunikationsWert: (typ: string) => string,
): Partial<Record<keyof BefundFelder, string>> {
  const werte: Partial<Record<keyof BefundFelder, string>> = {};
  for (const [key, ziel] of Object.entries(AKTE_ZIELE) as Array<
    [keyof BefundFelder, AkteZiel | null]
  >) {
    if (!ziel) continue;
    const wert =
      ziel.art === "feld" ? feldWert(ziel.feld) : kommunikationsWert(ziel.typ);
    werte[key] = String(wert ?? "").trim();
  }
  return werte;
}

/** Ein einzelner Schreibvorgang in die Maske — Ziel plus zu setzender Wert. */
export interface UebernahmeSchritt {
  ziel: AkteZiel;
  wert: string;
}

/**
 * Übersetzt die im Dialog gewählten Angaben in Schreibvorgänge auf der Maske.
 * Leere Werte und Felder ohne Ablage fallen heraus. Geschrieben wird damit in
 * den normalen Dirty-State — gespeichert wird über den bestehenden
 * Speichern-Knopf, nicht über einen eigenen Schreibpfad.
 */
export function uebernahmeSchritte(felder: Partial<BefundFelder>): UebernahmeSchritt[] {
  const schritte: UebernahmeSchritt[] = [];
  for (const [key, roh] of Object.entries(felder) as Array<
    [keyof BefundFelder, string | null | undefined]
  >) {
    const wert = String(roh ?? "").trim();
    if (!wert) continue;
    const ziel = AKTE_ZIELE[key];
    if (!ziel) continue;
    schritte.push({ ziel, wert });
  }
  return schritte;
}
